import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedConnection,
  fetchConnectionSettings as fetchConnectionSettings,
  selectConnectionSettings,
  selectSchemaCollectionAdded,
  selectTransactionSettingsDataChangeDetected,
  setTransactionSettingsDataChangeDetected,
} from "@/features/connections/integrationSlice";
import {
  Typography,
  Container,
  Card,
  Grid,
  TextField,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import "./GlobalSettings.css";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import ShepRadioButton from "@/components/GenericComponents/ShepRadioButton";
import { maximumSeparatorCharacterCode } from "@/utils/utils";
import ShepFormField from "@/components/GenericComponents/ShepFormField";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import { FooterButton } from "@/components/Integrations/ManageConnection/ManageConnectionPopupContext";

const DefaultFieldValues = {
  ElementSeparator: "*",
  SegmentTerminator: "~",
  ISA16: ">", //component separator
};

const AcknowledgementSettings = {
  0: "Acknowledge for each transaction set", // SHEP-756
  1: "Do not send acknowledgments",
  2: "Acknowledge for entire functional group", // SHEP-756
};

const CharSeparatorDescription = {
  ElementSeparator: "ElementSeparator",
  SegmentTerminator: "SegmentTerminator",
  ComponentSeparator: "ISA16",
  RepetitionSeparator: "ISA11",
};

const EditQualifierISAGS = forwardRef(({
  isTransactionSettings,
  selectedTransaction,
  onSave,
  setIsEditMode,
}, ref) => {
  const connectionSettings = useSelector(selectConnectionSettings);
  const dispatch = useDispatch();

  const selectedConnection = useSelector(selectSelectedConnection);
  const schemaCollectionAdded = useSelector(selectSchemaCollectionAdded);

  const [artifactType, setArtifactType] = useState(-1); //To represent the global
  const [endpointSchemaId, setEndpointSchemaId] = useState(0); //To represent the global

  const [elementSeparatorCharCode, setElementSeparatorCharCode] = useState(0);
  const [segmentTerminatorCharCode, setSegmentTerminatorCharCode] = useState(0);
  const [componentSeparatorCharCode, setComponentSeparatorCharCode] =
    useState(0);
  const [repetitionSeparatorCharCode, setRepetitionSeparatorCharCode] =
    useState(0);
  const [showTimeoutError, setShowTimeoutError] = useState(false);

  const [isInboundAcknowledgmentRequired, setIsInboundAcknowledgmentRequired] =
    useState(connectionSettings.IsInboundAcknowledgementRequired);
  const [formData, setFormData] = useState({});
  const refFormDataRef = useRef({});
  const dataChangeDetected = useSelector(
    selectTransactionSettingsDataChangeDetected
  );
  const dataChangeDetectedRef = useRef(dataChangeDetected);

  const showISAGSOptionalFieldCheckbox =
    !isTransactionSettings ||
    (isTransactionSettings && selectedTransaction?.Direction === "Inbound");

  //setting artifactType and endpointSchemaId for transaction level
  useEffect(() => {
    if (isTransactionSettings) {
      if (selectedTransaction) {
        setArtifactType(selectedTransaction.ArtifactType); //To represent the transaction set level
        setEndpointSchemaId(selectedTransaction.EndpointSchemaId); //To represent the transaction set level
      }
    }
  }, [isTransactionSettings, selectedTransaction]);

  //the method to fetch the ISAGS fields as when loading a page for global and transaction level
  useEffect(() => {
    if (selectedConnection.ConnectionId) {
      if (isTransactionSettings) {
        if (selectedTransaction) {
          dispatch(
            fetchConnectionSettings({
              connectionId: selectedConnection.ConnectionId,
              artifactType: selectedTransaction.ArtifactType,
              connectionArtifactSchemaId:
                selectedTransaction.ConnectionArtifactSchemaId,
            })
          );
        }
      } else if (!isTransactionSettings) {
        if (schemaCollectionAdded) {
          dispatch(
            fetchConnectionSettings({
              connectionId: selectedConnection.ConnectionId,
              artifactType: -1,
              connectionArtifactSchemaId: null,
            })
          );
        }
      }
    }
  }, [
    selectedConnection.ConnectionId,
    selectedTransaction,
    schemaCollectionAdded,
  ]);

  const handleSetIsInboundAcknowledgmentRequired = (event) => {
    const { name } = event.target;
    const value = !formData?.IsInboundAcknowledgementRequired;
    setIsInboundAcknowledgmentRequired(value);
    updateFormData(name, value);
  };

  const handleSetIsISAGSOptionalFieldsRequired = (event) => {
    const { name } = event.target;
    const value = !formData?.IsISAGSOptionalFieldRequired;
    updateFormData(name, value);
  };

  useEffect(() => {
    setShowTimeoutError(
      formData?.MaximumTimeoutInHours <= 0 && isInboundAcknowledgmentRequired
    );
  }, [formData?.MaximumTimeoutInHours, isInboundAcknowledgmentRequired]);

  useEffect(() => {
    return () =>
      dispatch(
        setTransactionSettingsDataChangeDetected({
          transactionSettingsDataChangeDetected: false,
        })
      );
  }, []);
  useEffect(() => {
    dataChangeDetectedRef.current = dataChangeDetected;
  }, [dataChangeDetected]);

  useEffect(() => {
    if (connectionSettings && Object.keys(connectionSettings).length > 0) {
      setFormData(connectionSettings);
      refFormDataRef.current = connectionSettings;
    }
  }, [connectionSettings]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    updateFormData(name, value);
  };

  const updateFormData = (name, value) => {
    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [name]: value,
      };
      const refData = refFormDataRef.current;
      const keys = Object.keys(refData);
      const changeRemains =
        keys.findIndex((key) => refData[key] != newData[key]) !== -1;
      if (changeRemains && !dataChangeDetectedRef.current) {
        dispatch(
          setTransactionSettingsDataChangeDetected({
            transactionSettingsDataChangeDetected: true,
          })
        );
      } else if (!changeRemains && dataChangeDetectedRef.current) {
        dispatch(
          setTransactionSettingsDataChangeDetected({
            transactionSettingsDataChangeDetected: false,
          })
        );
      }
      return newData;
    });
  };

  //Prevent decimal points

  const handleKeyDown = (event) => {
    // Block decimal point and 'e' (scientific notation)
    if (event.key === "." || event.key === "e") {
      event.preventDefault();
    }
    if (event.key === "-" || event.key === "e") {
      event.preventDefault();
    }
  };

  const {
    ElementSeparator,
    SegmentTerminator,
    ISA16: ComponentSeparator,
    ISA11: RepetitionSeparator,
  } = formData;
  useEffect(() => {
    if (ElementSeparator && ElementSeparator.length > 0) {
      const charCode = ElementSeparator.charCodeAt(0);
      if (charCode != elementSeparatorCharCode) {
        setElementSeparatorCharCode(charCode);
      }
    }
    if (SegmentTerminator && SegmentTerminator.length > 0) {
      const charCode = SegmentTerminator.charCodeAt(0);
      if (charCode != segmentTerminatorCharCode) {
        setSegmentTerminatorCharCode(charCode);
      }
    }
    if (ComponentSeparator && ComponentSeparator.length > 0) {
      const charCode = ComponentSeparator.charCodeAt(0);
      if (charCode != componentSeparatorCharCode) {
        setComponentSeparatorCharCode(charCode);
      }
    }
    if (RepetitionSeparator && RepetitionSeparator.length > 0) {
      const charCode = RepetitionSeparator.charCodeAt(0);
      if (charCode != repetitionSeparatorCharCode) {
        setRepetitionSeparatorCharCode(charCode);
      }
    }
  }, [
    ElementSeparator,
    SegmentTerminator,
    ComponentSeparator,
    RepetitionSeparator,
  ]);

  const isCharValueValid = (value) => {
    return !isNaN(value) && value >= 0 && value < maximumSeparatorCharacterCode;
  };
  const onCharCodeChange = (name, value, setCharCode) => {
    if (!isCharValueValid(value)) {
      return;
    }
    updateFormData(name, String.fromCharCode(value));
    setCharCode(value);
  };

  const onBack = () => {
    if (isTransactionSettings === true) {
      if (dataChangeDetected) {
        dispatch(
          createAlert({
            status: "Warning",
            alertType: "result",
            message:
              "There are unsaved changes remaining, do you wish to close the window?",
            buttonLabel: "Close",
            onButtonClick: () => setIsEditMode(false),
            buttonLabel2: "Cancel",
          })
        );
      } else {
        setIsEditMode(false);
      }
    }
  };
  const getUserAcceptsDefaults = async ({ message, numberValueFields }) => {
    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        message: message,
        buttonLabel: "OK",
        onButtonClick: () => saveSettings(numberValueFields),
        buttonLabel2: "Cancel",
      })
    );
  };

  const validateFields = () => {
    const dataValues = { ...formData };
    var errorMsg = "";
    //production settings validation
    if (
      dataValues.SenderISACodeQualifier === "" ||
      dataValues.SenderISACodeQualifier === null ||
      dataValues.SenderISACodeQualifier.length != 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " Qualifier (ISA05) under Production Settings must be a length of 2.\n";
    }
    if (
      dataValues.SenderISACode === "" ||
      dataValues.SenderISACode === null ||
      dataValues.SenderISACode.length > 15 ||
      dataValues.SenderISACode.length < 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " ISA ID (ISA06) under Production Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.ReceiverISACodeQualifier === "" ||
      dataValues.ReceiverISACodeQualifier === null ||
      dataValues.ReceiverISACodeQualifier.length != 2
    ) {
      errorMsg +=
        "Your Company's Qualifier (ISA07) under Production Settings must be a length of 2.\n";
    }
    if (
      dataValues.ReceiverISACode === "" ||
      dataValues.ReceiverISACode === null ||
      dataValues.ReceiverISACode.length > 15 ||
      dataValues.ReceiverISACode.length < 2
    ) {
      errorMsg +=
        "Your Company's ISA ID (ISA08) under Production Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.SenderGSCode === "" ||
      dataValues.SenderGSCode === null ||
      dataValues.SenderGSCode.length > 15 ||
      dataValues.SenderGSCode.length < 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " GS ID (GS02) under Production Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.ReceiverGSCode === "" ||
      dataValues.ReceiverGSCode === null ||
      dataValues.ReceiverGSCode.length > 15 ||
      dataValues.ReceiverGSCode.length < 2
    ) {
      errorMsg +=
        "Your Company's GS ID (GS03) under Production Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }

    //test settings validation
    if (
      dataValues.SenderISATestCodeQualifier === "" ||
      dataValues.SenderISATestCodeQualifier === null ||
      dataValues.SenderISATestCodeQualifier.length != 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " Qualifier (ISA05) under Test Settings must be a length of 2.\n";
    }
    if (
      dataValues.SenderISATestCode === "" ||
      dataValues.SenderISATestCode === null ||
      dataValues.SenderISATestCode.length > 15 ||
      dataValues.SenderISATestCode.length < 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " ISA ID (ISA06) under Test Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.ReceiverISATestCodeQualifier === "" ||
      dataValues.ReceiverISATestCodeQualifier === null ||
      dataValues.ReceiverISATestCodeQualifier.length != 2
    ) {
      errorMsg +=
        "Your Company's Qualifier (ISA07) under Test Settings must be a length of 2.\n";
    }
    if (
      dataValues.ReceiverISATestCode === "" ||
      dataValues.ReceiverISATestCode === null ||
      dataValues.ReceiverISATestCode.length > 15 ||
      dataValues.ReceiverISATestCode.length < 2
    ) {
      errorMsg +=
        "Your Company's ISA ID (ISA08) under Test Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.SenderGSTestCode === "" ||
      dataValues.SenderGSTestCode === null ||
      dataValues.SenderGSTestCode.length > 15 ||
      dataValues.SenderGSTestCode.length < 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " GS ID (GS02) under Test Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.ReceiverGSTestCode === "" ||
      dataValues.ReceiverGSTestCode === null ||
      dataValues.ReceiverGSTestCode.length > 15 ||
      dataValues.ReceiverGSTestCode.length < 2
    ) {
      errorMsg +=
        "Your Company's GS ID (GS03) under Test Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }

    if (errorMsg) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          message: errorMsg,
          buttonLabel: "Close",
        })
      );
      return Promise.reject(new Error(errorMsg));
    }

    const segmentTerminatorCharCodeIsValid = isCharValueValid(
      segmentTerminatorCharCode
    );
    const elementSeparatorCharCodeIsValid = isCharValueValid(
      elementSeparatorCharCode
    );
    const componentSeparatorCharCodeIsValid = isCharValueValid(
      componentSeparatorCharCode
    );
    const repetitionSeparatorCharCodeIsValid = isCharValueValid(
      repetitionSeparatorCharCode
    );

    const emptyFields = [];
    if (!segmentTerminatorCharCodeIsValid) {
      emptyFields.push("SegmentTerminator");
    }
    if (!elementSeparatorCharCodeIsValid) {
      emptyFields.push("ElementSeparator");
    }
    if (!componentSeparatorCharCodeIsValid) {
      emptyFields.push("ISA16");
    }

    const numberValueFields = {
      SegmentTerminator: segmentTerminatorCharCodeIsValid
        ? String.fromCharCode(segmentTerminatorCharCode)
        : DefaultFieldValues.SegmentTerminator,
      ElementSeparator: elementSeparatorCharCodeIsValid
        ? String.fromCharCode(elementSeparatorCharCode)
        : DefaultFieldValues.ElementSeparator,
      ISA16: componentSeparatorCharCodeIsValid
        ? String.fromCharCode(componentSeparatorCharCode)
        : DefaultFieldValues.ISA16,
      ISA11: repetitionSeparatorCharCodeIsValid
        ? String.fromCharCode(repetitionSeparatorCharCode)
        : "",
    };
    if (emptyFields.length > 0) {
      const defaultValues = emptyFields.map((x) => DefaultFieldValues[x]);
      let warningMessage =
        emptyFields.join(",") +
        " cannot be blank, using default values: " +
        defaultValues.join(",");
      getUserAcceptsDefaults({ message: warningMessage, numberValueFields });
      return Promise.resolve(true);
    } else {
      return saveSettings(numberValueFields);
    }
  };

  const saveSettings = (numberValueFields) => {
    // Used by save() for the TransactionSetSettings flow only.
    // Global settings saving is handled by GlobalSettings.handleSaveAll().
    return new Promise((resolve, reject) => {
      const {
        IsInboundAcknowledgementRequired,
        OutboundAcknowledgementSetting,
        MaximumTimeoutInHours,
        IsISAGSOptionalFieldRequired,
        ...isaGsValues
      } = formData;
      const connArtifactSchemaId = isTransactionSettings
        ? selectedTransaction.ConnectionArtifactSchemaId
        : null;
      var isaGsMap = isaGsValues;
      if (numberValueFields) {
        isaGsMap = {
          ...isaGsMap,
          ...numberValueFields,
        };
      }

      const payload = {
        ConnectionId: selectedConnection.ConnectionId.toString(),
        ArtifactType: artifactType,
        EndpointSchemaId: endpointSchemaId,
        IsTransactionSettings: isTransactionSettings,
        ConnectionSettings: {
          IsaGsMap: isaGsMap,
          OutboundAcknowledgementGroupSetting:
            OutboundAcknowledgementSetting ?? 0,
          IsInboundAcknowledgementRequired,
          MaximumTimeoutInHours,
          IsISAGSOptionalFieldRequired,
          ConnectionId: selectedConnection.ConnectionId.toString(),
          IsTransactionSettings: isTransactionSettings,
          ArtifactType: artifactType,
          EndpointSchemaId: endpointSchemaId,
          ConnectionArtifactSchemaId: connArtifactSchemaId,
        },
      };

      axios
        .post(
          "/connectionartifactschema/save-all-global-settings",
          payload
        )
        .then((response) => {
          if (response.data?.Success) {
            if (isTransactionSettings === true) {
              onSave();
              setIsEditMode(false);
            }
            dispatch(
              fetchConnectionSettings({
                connectionId: selectedConnection.ConnectionId,
                artifactType: artifactType,
                connectionArtifactSchemaId: connArtifactSchemaId,
              })
            );
            dispatch(
              setTransactionSettingsDataChangeDetected({
                transactionSettingsDataChangeDetected: false,
              })
            );

            window.heap.track("Transaction Settings - Save", {});
            resolve(true);
          } else {
            dispatch(
              createAlert({
                status: "Error",
                alertType: "result",
                message: "There was an error while saving the settings.",
                buttonLabel: "Close",
              })
            );
            resolve(false);
          }
        })
        .catch((error) => {
          let err = error.response?.data;
          if (err) {
            let message;
            if (typeof err === "string") {
              message = err;
            } else if (err.errors) {
              const errorMessages = Object.entries(err.errors)
                .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`)
                .join("\n");
              message = errorMessages || err.title || "There was an error while saving the settings.";
            } else {
              message = err.title ?? "There was an error while saving the settings.";
            }
            dispatch(
              createAlert({
                status: "Error",
                alertType: "result",
                message: message,
                buttonLabel: "Close",
              })
            );
          }
          reject(error);
        });
    });
  };

  const save = () => {
    // Used by TransactionSetSettingsGrid for the transaction settings flow only.
    // Global settings saving is handled by GlobalSettings.handleSaveAll().
    return validateFields();
  };

  const getFormData = () => {
    const segmentTerminatorCharCodeIsValid = isCharValueValid(
      segmentTerminatorCharCode
    );
    const elementSeparatorCharCodeIsValid = isCharValueValid(
      elementSeparatorCharCode
    );
    const componentSeparatorCharCodeIsValid = isCharValueValid(
      componentSeparatorCharCode
    );
    const repetitionSeparatorCharCodeIsValid = isCharValueValid(
      repetitionSeparatorCharCode
    );

    const numberValueFields = {
      SegmentTerminator: segmentTerminatorCharCodeIsValid
        ? String.fromCharCode(segmentTerminatorCharCode)
        : DefaultFieldValues.SegmentTerminator,
      ElementSeparator: elementSeparatorCharCodeIsValid
        ? String.fromCharCode(elementSeparatorCharCode)
        : DefaultFieldValues.ElementSeparator,
      ISA16: componentSeparatorCharCodeIsValid
        ? String.fromCharCode(componentSeparatorCharCode)
        : DefaultFieldValues.ISA16,
      ISA11: repetitionSeparatorCharCodeIsValid
        ? String.fromCharCode(repetitionSeparatorCharCode)
        : "",
    };

    const {
      IsInboundAcknowledgementRequired,
      OutboundAcknowledgementSetting,
      MaximumTimeoutInHours,
      IsISAGSOptionalFieldRequired,
      ...isaGsValues
    } = formData;

    const isaGsMap = {
      ...isaGsValues,
      ...numberValueFields,
    };

    return {
      IsaGsMap: isaGsMap,
      OutboundAcknowledgementGroupSetting:
        OutboundAcknowledgementSetting ?? 0,
      IsInboundAcknowledgementRequired,
      MaximumTimeoutInHours,
      IsISAGSOptionalFieldRequired,
      ConnectionId: selectedConnection.ConnectionId.toString(),
      IsTransactionSettings: isTransactionSettings,
      ArtifactType: artifactType,
      EndpointSchemaId: endpointSchemaId,
      ConnectionArtifactSchemaId: isTransactionSettings
        ? selectedTransaction?.ConnectionArtifactSchemaId
        : null,
    };
  };

  const validate = () => {
    const dataValues = { ...formData };
    var errorMsg = "";

    if (
      dataValues.SenderISACodeQualifier === "" ||
      dataValues.SenderISACodeQualifier === null ||
      dataValues.SenderISACodeQualifier?.length != 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " Qualifier (ISA05) under Production Settings must be a length of 2.\n";
    }
    if (
      dataValues.SenderISACode === "" ||
      dataValues.SenderISACode === null ||
      dataValues.SenderISACode?.length > 15 ||
      dataValues.SenderISACode?.length < 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " ISA ID (ISA06) under Production Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.ReceiverISACodeQualifier === "" ||
      dataValues.ReceiverISACodeQualifier === null ||
      dataValues.ReceiverISACodeQualifier?.length != 2
    ) {
      errorMsg +=
        "Your Company's Qualifier (ISA07) under Production Settings must be a length of 2.\n";
    }
    if (
      dataValues.ReceiverISACode === "" ||
      dataValues.ReceiverISACode === null ||
      dataValues.ReceiverISACode?.length > 15 ||
      dataValues.ReceiverISACode?.length < 2
    ) {
      errorMsg +=
        "Your Company's ISA ID (ISA08) under Production Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.SenderGSCode === "" ||
      dataValues.SenderGSCode === null ||
      dataValues.SenderGSCode?.length > 15 ||
      dataValues.SenderGSCode?.length < 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " GS ID (GS02) under Production Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.ReceiverGSCode === "" ||
      dataValues.ReceiverGSCode === null ||
      dataValues.ReceiverGSCode?.length > 15 ||
      dataValues.ReceiverGSCode?.length < 2
    ) {
      errorMsg +=
        "Your Company's GS ID (GS03) under Production Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }

    if (
      dataValues.SenderISATestCodeQualifier === "" ||
      dataValues.SenderISATestCodeQualifier === null ||
      dataValues.SenderISATestCodeQualifier?.length != 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " Qualifier (ISA05) under Test Settings must be a length of 2.\n";
    }
    if (
      dataValues.SenderISATestCode === "" ||
      dataValues.SenderISATestCode === null ||
      dataValues.SenderISATestCode?.length > 15 ||
      dataValues.SenderISATestCode?.length < 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " ISA ID (ISA06) under Test Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.ReceiverISATestCodeQualifier === "" ||
      dataValues.ReceiverISATestCodeQualifier === null ||
      dataValues.ReceiverISATestCodeQualifier?.length != 2
    ) {
      errorMsg +=
        "Your Company's Qualifier (ISA07) under Test Settings must be a length of 2.\n";
    }
    if (
      dataValues.ReceiverISATestCode === "" ||
      dataValues.ReceiverISATestCode === null ||
      dataValues.ReceiverISATestCode?.length > 15 ||
      dataValues.ReceiverISATestCode?.length < 2
    ) {
      errorMsg +=
        "Your Company's ISA ID (ISA08) under Test Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.SenderGSTestCode === "" ||
      dataValues.SenderGSTestCode === null ||
      dataValues.SenderGSTestCode?.length > 15 ||
      dataValues.SenderGSTestCode?.length < 2
    ) {
      errorMsg +=
        selectedConnection.ConnectionName +
        " GS ID (GS02) under Test Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }
    if (
      dataValues.ReceiverGSTestCode === "" ||
      dataValues.ReceiverGSTestCode === null ||
      dataValues.ReceiverGSTestCode?.length > 15 ||
      dataValues.ReceiverGSTestCode?.length < 2
    ) {
      errorMsg +=
        "Your Company's GS ID (GS03) under Test Settings must be with a minimum length of 2 and a maximum length of 15.\n";
    }

    return errorMsg;
  };

  const onSaveSuccess = () => {
    refFormDataRef.current = { ...formData };
    dispatch(
      setTransactionSettingsDataChangeDetected({
        transactionSettingsDataChangeDetected: false,
      })
    );
  };

  useImperativeHandle(ref, () => ({
    save,
    getFormData,
    validate,
    onSaveSuccess,
  }));

  const padString = (text, length, char) => {
    if (!text) {
      text = "_";
      return text.repeat(length);
    }
    return text.padEnd(length, char);
  };

  // For config Preview section
  let date = new Date();
  let yy = date.getYear() - 100;
  let MM = (date.getMonth() + 1).toString().padStart(2, "0");
  let dd = date.getDate().toString().padStart(2, "0");
  let _ = formData?.ElementSeparator === "" ? "*" : formData?.ElementSeparator;
  let t =
    formData?.SegmentTerminator === "" ? "~" : formData?.SegmentTerminator;

  return (
    <Container>
      <Card className="global-settings-card">
        <Grid
          container
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          sx={{
            justifyContent: "center",
          }}
        >
          {/* </Grid> */}
          {/** tranaction row info */}
          {isTransactionSettings && selectedTransaction && (
            <>
              <Grid item xs={1.5} />
              <Grid item xs={6}>
                <Typography variant="h2" sx={{ marginBottom: "20px" }}>
                  {`Editing schema: ${selectedTransaction?.SchemaName} / Document: ${selectedTransaction?.Document} / Description: ${selectedTransaction?.DocumentDescription}`}
                </Typography>
              </Grid>
              <Grid item xs={3} />
              <Grid item xs={1.5} />
            </>
          )}
          {/* Header */}
          <Grid item xs={1.5} />
          <Grid item xs={3}>
          </Grid>
          <Grid item xs={3}>
            {/* Footer button rendered via portal to FooterBarHost */}
            {isTransactionSettings && (
              <FooterButton variant="secondary" onClick={onBack}>
                Back
              </FooterButton>
            )}
          </Grid>
          <Grid item xs={3} />
          <Grid item xs={1.5} />
          {/* Production Settings and Test Settings */}
          <Grid item xs={1.5}></Grid>
          <Grid item xs={4.5}>
            <Typography
              variant="h3"
              sx={{ marginBottom: "20px", marginTop: "50px" }}
            >
              Production Settings
            </Typography>
          </Grid>
          <Grid item xs={4.5}>
            <Typography
              variant="h3"
              sx={{ marginBottom: "20px", marginTop: "50px" }}
            >
              Test Settings
            </Typography>
          </Grid>
          <Grid item xs={1.5}></Grid>

          {/* Row 1 labels */}
          <Grid item xs={1.5}></Grid>
          <Grid item xs={2.1}>
            <Typography>
              {selectedConnection.ConnectionName} Qualifier
            </Typography>
            <Typography>
              (ISA05){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={2.1}>
            <Typography>Your Company's Qualifier</Typography>
            <Typography>
              (ISA07){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={0.3}></Grid>
          <Grid item xs={2.1}>
            <Typography>
              {selectedConnection.ConnectionName} Qualifier
            </Typography>
            <Typography>
              (ISA05){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={2.1}>
            <Typography>Your Company's Qualifier</Typography>
            <Typography>
              (ISA07){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={1.8} />

          {/* Row 1 inputs */}
          <Grid item xs={1.5} style={{ marginTop: "16px" }} />
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="SenderISACodeQualifier"
              value={formData?.SenderISACodeQualifier || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="ReceiverISACodeQualifier"
              value={formData?.ReceiverISACodeQualifier || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={0.3}></Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="SenderISATestCodeQualifier"
              value={formData?.SenderISATestCodeQualifier || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="ReceiverISATestCodeQualifier"
              value={formData?.ReceiverISATestCodeQualifier || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8} />

          {/* Row 2 labels */}
          <Grid item xs={1.5}></Grid>
          <Grid item xs={2.1}>
            <Typography>{selectedConnection.ConnectionName} ISA ID</Typography>
            <Typography>
              (ISA06){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={2.1}>
            <Typography>Your Company's ISA ID</Typography>
            <Typography>
              (ISA08){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={0.3}></Grid>
          <Grid item xs={2.1}>
            <Typography>{selectedConnection.ConnectionName} ISA ID</Typography>
            <Typography>
              (ISA06){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={2.1}>
            <Typography>Your Company's ISA ID</Typography>
            <Typography>
              (ISA08){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={1.8} />

          {/* Row 2 inputs */}
          <Grid item xs={1.5}></Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="SenderISACode"
              value={formData?.SenderISACode || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="ReceiverISACode"
              value={formData?.ReceiverISACode || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={0.3}></Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="SenderISATestCode"
              value={formData?.SenderISATestCode || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="ReceiverISATestCode"
              value={formData?.ReceiverISATestCode || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8} />

          {/* Row 3 labels */}
          <Grid item xs={1.5}></Grid>
          <Grid item xs={2.1}>
            <Typography>{selectedConnection.ConnectionName} GS ID</Typography>
            <Typography>
              (GS02){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={2.1}>
            <Typography>Your Company's GS ID</Typography>
            <Typography>
              (GS03){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={0.3}></Grid>
          <Grid item xs={2.1}>
            <Typography>{selectedConnection.ConnectionName} GS ID</Typography>
            <Typography>
              (GS02){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={2.1}>
            <Typography>Your Company's GS ID</Typography>
            <Typography>
              (GS03){" "}
              <span
                style={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
              >
                *
              </span>
            </Typography>
          </Grid>
          <Grid item xs={1.8} />

          {/* Row 3 inputs */}
          <Grid item xs={1.5} style={{ marginTop: "16px" }}></Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="SenderGSCode"
              value={formData?.SenderGSCode || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="ReceiverGSCode"
              value={formData?.ReceiverGSCode || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={0.3}></Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="SenderGSTestCode"
              value={formData?.SenderGSTestCode || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={2.1}>
            <TextField
              variant="outlined"
              name="ReceiverGSTestCode"
              value={formData?.ReceiverGSTestCode || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8} />
        </Grid>
      </Card>

      <Card className="global-settings-card">
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={1.5} />
          <Grid item xs={3.5} marginBottom={5} marginTop={5}>
            {showISAGSOptionalFieldCheckbox ? (
              <FormControlLabel
                onChange={handleSetIsISAGSOptionalFieldsRequired}
                checked={formData?.IsISAGSOptionalFieldRequired}
                name="IsISAGSOptionalFieldRequired"
                label="Use ISA/GS Optional Fields For Inbound EDI Connection Matching"
                control={<ShepCheckbox />}
              />
            ) : (
              <></> // empty placeholder
            )}
          </Grid>
          <Grid item xs={2}></Grid>
          <Grid item xs={3}></Grid>
          <Grid item xs={1.5} />

          {/* Row 1 labels */}
          <Grid item xs={1.5} />
          <Grid item xs={1.8}>
            <Typography>Authorization Information</Typography>
            <Typography>{"Qualifier (ISA01)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Authorization Information</Typography>
            <Typography>{"(ISA02)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Security Information</Typography>
            <Typography>{"Qualifier (ISA03)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Security Information</Typography>
            <Typography>{"(ISA04)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Component Element</Typography>
            <Typography>{"Separator (ISA16)"}</Typography>
          </Grid>
          <Grid item xs={1.5} />

          {/* Row 1 inputs */}
          <Grid item xs={1.5} />
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="ISA01"
              value={formData?.ISA01 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="ISA02"
              value={formData?.ISA02 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="ISA03"
              value={formData?.ISA03 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="ISA04"
              value={formData?.ISA04 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name={CharSeparatorDescription.ComponentSeparator}
              value={formData?.ISA16 || ""}
              onChange={handleChange}
              sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
            />{" "}
            <TextField
              variant="outlined"
              value={componentSeparatorCharCode}
              onChange={(event) =>
                onCharCodeChange(
                  CharSeparatorDescription.ComponentSeparator,
                  event.target.value,
                  setComponentSeparatorCharCode
                )
              }
              sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.5} />

          {/* Row 2 labels */}
          <Grid item xs={1.5} />
          <Grid item xs={1.8}>
            <Typography>Repetition Separator</Typography>
            <Typography>{"(ISA11)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Interchange Control Version</Typography>
            <Typography>{"Number (ISA12)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Acknowledgment Requested</Typography>
            <Typography>{"(ISA14)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Usage Indicator</Typography>
            <Typography>{"(ISA15)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Element Separator</Typography>
          </Grid>
          <Grid item xs={1.5} />

          {/* Row 2 inputs */}
          <Grid item xs={1.5} />
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name={CharSeparatorDescription.RepetitionSeparator}
              value={formData?.ISA11 || ""}
              onChange={handleChange}
              sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
            />{" "}
            <TextField
              variant="outlined"
              value={repetitionSeparatorCharCode}
              onChange={(event) =>
                onCharCodeChange(
                  CharSeparatorDescription.RepetitionSeparator,
                  event.target.value,
                  setRepetitionSeparatorCharCode
                )
              }
              sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="ISA12"
              value={formData?.ISA12 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="ISA14"
              value={formData?.ISA14 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="ISA15"
              value={formData?.ISA15 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name={CharSeparatorDescription.ElementSeparator}
              value={formData?.ElementSeparator || ""}
              onChange={handleChange}
              sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
            />{" "}
            <TextField
              variant="outlined"
              value={elementSeparatorCharCode}
              onChange={(event) =>
                onCharCodeChange(
                  CharSeparatorDescription.ElementSeparator,
                  event.target.value,
                  setElementSeparatorCharCode
                )
              }
              sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.5} />

          {/* Row 3 labels */}
          <Grid item xs={1.5} />
          <Grid item xs={1.8}>
            <Typography>Segment Terminator</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Agency Code</Typography>
            <Typography>{"(GS07)"}</Typography>
          </Grid>
          <Grid item xs={1.8}>
            <Typography>Version/Release/Industry</Typography>
            <Typography>{"Identifier Code (GS08)"}</Typography>
          </Grid>
          <Grid item xs={3.6} />
          <Grid item xs={1.5} />

          {/* Row 3 inputs */}
          <Grid item xs={1.5} />
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name={CharSeparatorDescription.SegmentTerminator}
              value={formData?.SegmentTerminator || ""}
              onChange={handleChange}
              sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
            />{" "}
            <TextField
              variant="outlined"
              value={segmentTerminatorCharCode}
              onChange={(event) =>
                onCharCodeChange(
                  CharSeparatorDescription.SegmentTerminator,
                  event.target.value,
                  setSegmentTerminatorCharCode
                )
              }
              sx={{ width: 90, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="GS07"
              value={formData?.GS07 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={1.8}>
            <TextField
              variant="outlined"
              name="GS08"
              value={formData?.GS08 || ""}
              onChange={handleChange}
              sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
            />
          </Grid>
          <Grid item xs={3.6} />
          <Grid item xs={1.5} />
        </Grid>
      </Card>

      <Card className="global-settings-card">
        <Grid
          container
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          sx={{
            justifyContent: "center",
          }}
        >
          {/** Header row */}
          <Grid item xs={1.5} />
          <Grid item xs={9}>
            <Typography variant="h2" sx={{ marginBottom: "20px" }}>
              Preview
            </Typography>
          </Grid>
          <Grid item xs={1.5} />
          {/** preview row */}
          <Grid item xs={1.5} />
          <Grid item xs={9}>
            <div className="conn-settings-preview-container">
              <div
                className="preview-isa-gs"
                style={{
                  padding: "10px 0",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                ISA{_}
                {padString(formData?.ISA01, 2, " ")}
                {_}
                {padString(formData?.ISA02, 10, " ")}
                {_}
                {padString(formData?.ISA03, 2, " ")}
                {_}
                {padString(formData?.ISA04, 10, " ")}
                {_}
                {padString(formData?.SenderISACodeQualifier, 2, " ")}
                {_}
                {padString(formData?.SenderISACode, 15, " ")}
                {_}
                {padString(formData?.ReceiverISACodeQualifier, 2, " ")}
                {_}
                {padString(formData?.ReceiverISACode, 15, " ")}
                {_}
                {yy}
                {MM}
                {dd}
                {_}1300{_}
                {padString(formData?.ISA11, 1, " ")}
                {_}
                {padString(formData?.ISA12, 5, " ")}
                {_}
                {yy}
                {MM}
                {dd}001{_}
                {padString(formData?.ISA14, 1, " ")}
                {_}
                {padString(formData?.ISA15, 1, " ")}
                {_}
                {padString(formData?.ISA16, 1, " ")}
                {t}
                GS{_}PO{_}
                {padString(formData?.SenderGSCode, 2, " ")}
                {_}
                {padString(formData?.ReceiverGSCode, 2, " ")}
                {_}
                {yy}
                {MM}
                {dd}
                {_}1300{_}
                {yy}
                {MM}
                {dd}001{_}
                {padString(formData?.GS07, 1, " ")}
                {_}
                {padString(formData?.GS08, 1, " ")}
                {t}
              </div>
            </div>
          </Grid>
          <Grid item xs={1.5} />
        </Grid>
      </Card>
      {/* Acknowledgement Settings*/}
      <Card className="global-settings-card">
        <Grid
          container
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          sx={{
            justifyContent: "center",
          }}
        >
          {/** acknowledgment settings row*/}
          <Grid item xs={1.5} />
          <Grid item xs={9}>
            <Typography
              variant="h2"
              sx={{ marginBottom: "20px", marginTop: "50px" }}
            >
              Inbound Acknowledgement Settings
            </Typography>
            <FormControlLabel
              onChange={handleSetIsInboundAcknowledgmentRequired}
              checked={formData?.IsInboundAcknowledgementRequired}
              name={"IsInboundAcknowledgementRequired"}
              label={"Require Acknowledgement After Sending Files"}
              control={<ShepCheckbox />}
            />
            <Typography sx={{ marginTop: "20px" }}>
              Maximum Acknowledgement Timeout (In Hours){" "}
            </Typography>
            <ShepFormField
              value={formData?.MaximumTimeoutInHours}
              onChange={handleChange}
              disabled={!isInboundAcknowledgmentRequired}
              error={showTimeoutError}
              helperText="Timeout must be greater than 0"
              type="number"
              name="MaximumTimeoutInHours"
              inputProps={{ step: 1 }}
              onKeyDown={handleKeyDown}
            ></ShepFormField>
            <Grid item xs={1.5} />
            <Typography
              variant="h2"
              sx={{ marginBottom: "20px", marginTop: "20px" }}
            >
              Outbound Acknowledgement Settings
            </Typography>
            <RadioGroup
              value={formData?.OutboundAcknowledgementSetting ?? 0}
              name="OutboundAcknowledgementSetting"
              onChange={handleChange}
            >
              {Object.keys(AcknowledgementSettings).map((key) => (
                <FormControlLabel
                  key={key}
                  value={key}
                  id={key}
                  label={AcknowledgementSettings[key]}
                  control={<ShepRadioButton />}
                />
              ))}
            </RadioGroup>
          </Grid>
          <Grid item xs={1.5} />
        </Grid>
      </Card>
    </Container>
  );
});
export default EditQualifierISAGS;
